// IDCAP-Transport fuer LG WebOS - parallel zu HCAP (helpers/Cast/LG.js).
//
// LG stellt HCAP perspektivisch ein und empfiehlt IDCAP als Nachfolger. Beide
// koennen gleichzeitig laufen: es sind getrennte Daemons auf getrennten Ports
// (HCAP ws://127.0.0.1:8053/hcap_command, IDCAP ws://127.0.0.1:8153/hcap_command)
// mit getrennten Globals (hcap / idcap). Diese Datei bringt IDCAP mit, misst
// aus, ob das Geraet es beherrscht, und stellt dieselben Operationen bereit -
// aktiv geschaltet wird es aber (noch) nicht, siehe CastLG.
//
// Unterschiede zur HCAP-JS-API, die beim Umstellen zaehlen:
//  - Ein einziger Einstiegspunkt: idcap.request(command, {parameters, ...})
//    statt hcap.<namespace>.<methode>({...}). Der Command ist eine URL.
//  - idcap.request liefert bereits ein Promise (onSuccess/onFailure gibt es
//    zusaetzlich). Ein Timeout fehlt trotzdem - siehe request() unten.
//  - application und preloadedApplication sind zu EINEM Namespace verschmolzen:
//    idcap://application/list liefert beides, idcap://application/launch startet
//    beides. Die HCAP-Sonderbehandlung numerischer Preloaded-Ids entfaellt.
//  - Der App-Eintrag heisst installed (Bool) statt valid, und traegt zusaetzlich
//    token: notRequired | authSuccess | authNeeded - also genau die Auskunft,
//    ob eine OTT-App noch eine Lizenz-/Token-Freischaltung braucht.
//
// Command-Namen und Parameter sind der IDCAP-Partnerdoku entnommen
// (IDCAP_DOC_PORTABLE, API Mapping Guide), nicht geraten.

// HCAP -> IDCAP, wie im offiziellen API Mapping Guide aufgefuehrt.
export const IDCAP = {
  // hcap.application.getApplicationList + preloadedApplication.getPreloadedApplicationList
  APP_LIST: 'idcap://application/list',
  // hcap.application.launchApplication + preloadedApplication.launchPreloadedApplication
  APP_LAUNCH: 'idcap://application/launch',
  APP_INSTALL: 'idcap://application/install',
  APP_UNINSTALL: 'idcap://application/uninstall',
  // hcap.property.getProperty / setProperty
  PROPERTY_GET: 'idcap://configuration/property/get',
  PROPERTY_SET: 'idcap://configuration/property/set',
  SERVICE_COUNTRY_GET: 'idcap://configuration/servicecountry/get',
  // hcap.network.getNetworkInformation
  NETWORK_GET: 'idcap://network/configuration/get',
  // hcap.network.getSoftAP / setSoftAP -> das sind die ADVANCED-Varianten
  // (channel/VLAN/mode). Die schlanken softap/get|set MIT ssid+securityKey sind
  // laut Doku Signage-only und auf Hospitality-TVs nicht verfuegbar.
  SOFTAP_ADV_GET: 'idcap://network/softap/advanced/get',
  SOFTAP_ADV_SET: 'idcap://network/softap/advanced/set',
  SOFTAP_GET: 'idcap://network/softap/get',
  SOFTAP_SET: 'idcap://network/softap/set',
  // hcap.checkout.requestCheckout / takeCheckoutSnapshot
  CHECKOUT_REQUEST: 'idcap://tv/checkout/request',
  CHECKOUT_SNAPSHOT: 'idcap://tv/checkout/snapshot',
  // hcap.system.startManualUpdate / getUpdateProgress / cancelUpdate
  FW_START: 'idcap://system/forcesfirmware/upgrade/start',
  FW_CANCEL: 'idcap://system/forcesfirmware/upgrade/cancel',
  FW_PROGRESS: 'idcap://system/forcesfirmware/progress/get',
  // hcap.system.requestCloning
  CLONING_IMPORT: 'idcap://system/cloningdata/import',
  // hcap.power.reboot
  POWER_COMMAND: 'idcap://power/command',
  // hcap.mode.getHcapMode / setHcapMode
  IDCAP_MODE_GET: 'idcap://configuration/idcapmode/get',
};

function logErr(scope, f) {
  const msg = (f && (f.errorMessage || f.errorText || f.message)) || String(f);
  console.log('[Cast/IDCAP] ' + scope + ' error: ' + msg);
}

export function hasIdcap() {
  return typeof idcap !== 'undefined' && !!idcap && typeof idcap.request === 'function';
}

// Ein IDCAP-Kommando absetzen.
//
// Der Timeout ist nicht optional: idcap.js gibt nach vier gescheiterten
// WebSocket-Versuchen auf (isMaxCount:4) und laesst danach jedes eingereihte
// Kommando unbeantwortet liegen. Ohne Timeout haengt eine Promise.all-Erkennung
// dann fuer immer - derselbe Grund wie im HCAP-Wrapper.
export function request(command, parameters, timeoutMs) {
  return new Promise((resolve, reject) => {
    if (!hasIdcap()) {
      reject(new Error('idcap missing'));
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(command + ' timeout'));
    }, timeoutMs || 5000);
    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(arg);
    };
    try {
      // parameters ist Pflicht - idcap.js verlangt ein Objekt und bricht bei
      // Arrays bzw. undefined ohne Rueckmeldung ab.
      const p = idcap.request(command, { parameters: parameters || {} });
      // Bei ungueltigem Command loggt idcap.js nur und liefert undefined
      // zurueck, statt zu werfen. Ohne diese Pruefung liefe der Aufruf in ein
      // "Cannot read properties of undefined (reading 'then')".
      if (!p || typeof p.then !== 'function') {
        finish(reject, new Error(command + ' rejected by idcap.js'));
        return;
      }
      p.then(
        (s) => finish(resolve, s),
        (f) => {
          logErr(command, f);
          finish(reject, f);
        }
      );
    } catch (e) {
      logErr(command, e);
      finish(reject, e);
    }
  });
}

export default class Idcap {
  // Ergebnis der Verfuegbarkeitspruefung, siehe probe().
  static status() {
    return (
      Idcap._status || {
        probed: false,
        loaded: hasIdcap(),
        working: false,
        apiVersion: null,
        mode: null,
        detail: 'nicht geprueft',
      }
    );
  }

  // Prueft EINMAL, ob IDCAP auf diesem Geraet tatsaechlich antwortet.
  //
  // Als Test dient idcapmode/get: parameterlos, ohne Nebenwirkung, und laut
  // Doku auf jedem Commercial TV vorhanden. Antwortet das, steht der Daemon und
  // die Umstellung von HCAP auf IDCAP ist auf diesem Geraet moeglich.
  static probe(force) {
    if (Idcap._probePromise && !force) return Idcap._probePromise;

    const base = {
      probed: true,
      loaded: hasIdcap(),
      working: false,
      apiVersion: (typeof idcap !== 'undefined' && idcap && idcap.API_VERSION) || null,
      mode: null,
      detail: '',
    };

    if (!hasIdcap()) {
      Idcap._status = Object.assign(base, { detail: 'idcap.js nicht geladen' });
      Idcap._probePromise = Promise.resolve(Idcap._status);
      return Idcap._probePromise;
    }

    Idcap._probePromise = request(IDCAP.IDCAP_MODE_GET, {})
      .then((s) => {
        Idcap._status = Object.assign(base, {
          working: true,
          mode: s && typeof s.mode !== 'undefined' ? s.mode : null,
          detail: 'ok',
        });
        console.log(
          '[Cast/IDCAP] verfuegbar: version=' + base.apiVersion + ' mode=' + Idcap._status.mode
        );
        return Idcap._status;
      })
      .catch((e) => {
        Idcap._status = Object.assign(base, {
          detail: String((e && (e.errorMessage || e.message)) || e),
        });
        console.log('[Cast/IDCAP] nicht verfuegbar: ' + Idcap._status.detail);
        return Idcap._status;
      });

    return Idcap._probePromise;
  }

  // ---- Operationen (Signaturen wie in CastLG, damit ein Umschalten spaeter
  //      nur den Aufrufer betrifft und nicht die Aufrufstellen) --------------

  // Liefert Apps im selben Schema wie CastLG._normalizeAppList:
  // {id, name, version, type, valid, visible}. installed -> valid, damit die
  // Install-Seite ohne Sonderfall funktioniert; token kommt zusaetzlich mit,
  // weil daran haengt, ob eine OTT-App noch freigeschaltet werden muss.
  static getApplicationList(extraInfo) {
    return request(IDCAP.APP_LIST, extraInfo === false ? {} : { extraInfo: true }).then((s) => {
      const list = (s && s.list) || [];
      return list.map((a) => ({
        id: String(a.appId || ''),
        name: a.title || '',
        version: a.version || '',
        type: a.type,
        valid: typeof a.installed === 'boolean' ? a.installed : undefined,
        visible: undefined,
        removable: a.removable,
        // notRequired | authSuccess | authNeeded
        token: a.token,
      }));
    });
  }

  static launchApp(appId, params) {
    const p = { id: String(appId) };
    if (params) p.params = params;
    return request(IDCAP.APP_LAUNCH, p).then(() => appId);
  }

  static installApp(appId) {
    return request(IDCAP.APP_INSTALL, { id: String(appId) }, 120000);
  }

  static getProperty(key) {
    return request(IDCAP.PROPERTY_GET, { key: key }).then((s) => {
      if (!s) return null;
      // Die Rueckgabe ist laut Doku property-abhaengig ("ReturnValueMayVary"):
      // mal {value}, mal der Key selbst als Feld.
      if (typeof s.value !== 'undefined') return s.value;
      if (typeof s[key] !== 'undefined') return s[key];
      return s;
    });
  }

  static setProperty(key, value) {
    return request(IDCAP.PROPERTY_SET, { key: key, value: String(value) });
  }

  static getNetworkInformation() {
    return request(IDCAP.NETWORK_GET, {}).then((s) => {
      if (!s) return null;
      return {
        ip: s.ipAddress || s.ip_address || s.ip || '',
        ssid: s.ssid || '',
        key: s.wifiPassKey || s.wifi_pass_key || '',
        securityType: s.securityType || s.security_type || '',
        mode: s.networkMode || s.network_mode || s.mode || '',
        raw: s,
      };
    });
  }

  static getServiceCountry() {
    return request(IDCAP.SERVICE_COUNTRY_GET, {}).then(
      (s) => (s && (s.country || s.serviceCountry)) || null
    );
  }

  static requestCheckout() {
    return request(IDCAP.CHECKOUT_REQUEST, {}, 60000);
  }

  static takeCheckoutSnapshot() {
    return request(IDCAP.CHECKOUT_SNAPSHOT, {}, 60000);
  }

  static reboot() {
    // hcap.power.reboot entspricht power/command mit "reboot".
    return request(IDCAP.POWER_COMMAND, { command: 'reboot' });
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Cast/Idcap.js